import type {
  Brand,
  BrandCoverageWire,
  BrandResolution,
  RunSummary,
} from "@crux/contracts";
import { api } from "../client.js";
import { problem } from "./errors.js";

/**
 * Brands, and the workspaces around them.
 */

export async function fetchRuns(workspaceId: string): Promise<readonly RunSummary[]> {
  const { data, response } = await api.GET("/api/workspaces/{workspaceId}/runs", {
    params: { path: { workspaceId }, query: {} },
  });
  if (data === undefined) {
    throw new Error(`runs request failed with ${String(response.status)}`);
  }
  return data.runs;
}

export async function fetchBrandCoverage(
  workspaceId: string,
  brandId: string,
): Promise<BrandCoverageWire> {
  const { data, response } = await api.GET(
    "/api/workspaces/{workspaceId}/brands/{brandId}/coverage",
    { params: { path: { workspaceId, brandId } } },
  );
  if (data === undefined) {
    throw new Error(`coverage request failed with ${String(response.status)}`);
  }
  return data;
}

export async function fetchBrands(workspaceId: string): Promise<readonly Brand[]> {
  const response = await api.GET("/api/workspaces/{workspaceId}/brands", {
    params: { path: { workspaceId } },
  });
  if (response.data === undefined) {
    throw new Error(`brands request failed with ${String(response.response.status)}`);
  }
  return response.data.brands;
}

export async function createBrand(
  workspaceId: string,
  body: { name: string; website: string; aliases: string[] },
): Promise<BrandResolution> {
  const response = await api.POST("/api/workspaces/{workspaceId}/brands", {
    params: { path: { workspaceId } },
    body,
  });
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "add that brand"));
  }
  return response.data;
}

export async function updateBrand(
  workspaceId: string,
  brandId: string,
  body: {
    name?: string;
    website?: string;
    aliases?: string[];
    otherDomains?: string[];
    country?: string;
    language?: string;
  },
): Promise<Brand> {
  const response = await api.PATCH("/api/workspaces/{workspaceId}/brands/{brandId}", {
    params: { path: { workspaceId, brandId } },
    body,
  });
  if (response.data === undefined) {
    const status = response.response.status;
    if (status === 409) {
      throw new Error(
        "Another brand in this workspace already owns one of those sites. Remove it there first — a site can only identify one company.",
      );
    }
    if (status === 400) {
      throw new Error(
        "One of those sites is not a website we can identify a company by.",
      );
    }
    throw new Error(problem(status, "save those changes"));
  }
  return response.data;
}

export async function renameWorkspace(
  workspaceId: string,
  name: string,
): Promise<{ id: string; name: string }> {
  const response = await api.PATCH("/api/workspaces/{workspaceId}", {
    params: { path: { workspaceId } },
    body: { name },
  });
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "rename this workspace"));
  }
  return response.data;
}

/** A new workspace the person asking owns (plans/google-auth-and-invites.md, D6). */
export async function createWorkspace(
  name: string,
): Promise<{ id: string; name: string }> {
  const { data, response } = await api.POST("/api/workspaces", { body: { name } });
  if (data !== undefined) return data;
  if (response.status === 400) throw new Error("Give the workspace a name.");
  if (response.status === 429) {
    throw new Error("Too many new workspaces in an hour. Try again later.");
  }
  throw new Error(problem(response.status, "create the workspace"));
}
